import React from "react";
import {
  Box,
  ClickAwayListener,
  FormHelperText,
  InputLabel,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import ImageOutlined from "@mui/icons-material/ImageOutlined";
import VideocamOutlined from "@mui/icons-material/VideocamOutlined";
import {
  filterMentionAssets,
  parsePromptSegments,
} from "../mentionAssets";

const CHIP_IMAGE = "#0E7490"; // teal
const CHIP_VIDEO = "#C2410C"; // burnt orange

function chipColors(kind, theme) {
  const base = kind === "video" ? CHIP_VIDEO : CHIP_IMAGE;
  return {
    color: base,
    bgcolor: alpha(base, theme.palette.mode === "dark" ? 0.22 : 0.12),
    border: `1px solid ${alpha(base, 0.35)}`,
  };
}

function MentionThumb({ asset, size = 56 }) {
  if (!asset?.previewUrl) return null;
  if (asset.kind === "video") {
    return (
      <Box
        component="video"
        src={asset.previewUrl}
        muted
        playsInline
        preload="metadata"
        sx={{
          width: size,
          height: size,
          objectFit: "cover",
          borderRadius: 1,
          display: "block",
          bgcolor: "#000",
        }}
      />
    );
  }
  return (
    <Box
      component="img"
      src={asset.previewUrl}
      alt=""
      sx={{
        width: size,
        height: size,
        objectFit: "cover",
        borderRadius: 1,
        display: "block",
      }}
    />
  );
}

function HoverPreviewCard({ asset, anchorEl }) {
  const theme = useTheme();
  if (!asset || !anchorEl) return null;
  return (
    <Popper
      open
      anchorEl={anchorEl}
      placement="top"
      modifiers={[{ name: "offset", options: { offset: [0, 8] } }]}
      sx={{ zIndex: theme.zIndex.tooltip + 2 }}
    >
      <Paper
        elevation={8}
        sx={{
          p: 1,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          maxWidth: 200,
        }}
      >
        <Stack spacing={0.75} alignItems="center">
          <MentionThumb asset={asset} size={120} />
          <Typography variant="caption" fontWeight={700} textAlign="center">
            {asset.label}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {asset.kind === "video" ? "Video reference" : "Image reference"}
          </Typography>
        </Stack>
      </Paper>
    </Popper>
  );
}

function placeCaretAtEnd(el) {
  const range = document.createRange();
  const sel = window.getSelection();
  range.selectNodeContents(el);
  range.collapse(false);
  sel?.removeAllRanges();
  sel?.addRange(range);
}

function getTextBeforeCaret(root) {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || !root.contains(sel.anchorNode)) return "";
  const range = sel.getRangeAt(0);
  const pre = range.cloneRange();
  pre.selectNodeContents(root);
  pre.setEnd(range.endContainer, range.endOffset);
  // Must use the same Range serialization for length + content (innerText drifts with <div>/<br>).
  return pre.toString();
}

/** Walk the caret backward `count` UTF-16 units and delete that range. */
function deleteCharsBeforeCaret(root, count) {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || count <= 0 || !root.contains(sel.anchorNode)) return;
  sel.collapseToEnd();
  for (let i = 0; i < count; i += 1) {
    try {
      sel.modify("extend", "backward", "character");
    } catch {
      break;
    }
  }
  if (!sel.isCollapsed) {
    sel.deleteFromDocument();
  }
}

/**
 * Professional @-mention prompt editor.
 * Chips are colored; hover shows a media thumbnail; typing @ opens an asset picker.
 */
export default function MentionPromptInput({
  value = "",
  onChange,
  assets = [],
  label,
  helperText,
  placeholder,
  minRows = 4,
  disabled = false,
  lang,
  embedded = false,
}) {
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";
  const editorRef = React.useRef(null);
  const mirrorRef = React.useRef(null);
  const skipSyncRef = React.useRef(false);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [menuQuery, setMenuQuery] = React.useState("");
  const [menuIndex, setMenuIndex] = React.useState(0);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [hover, setHover] = React.useState({ asset: null, el: null });
  const mentionStartRef = React.useRef(null); // caret index of '@'

  const assetByToken = React.useMemo(() => {
    const map = new Map();
    for (const a of assets) map.set(a.token, a);
    return map;
  }, [assets]);

  const filtered = React.useMemo(
    () => filterMentionAssets(assets, menuQuery),
    [assets, menuQuery],
  );

  const buildDomFromValue = React.useCallback(
    (text) => {
      const root = document.createDocumentFragment();
      const parts = parsePromptSegments(text);
      for (const part of parts) {
        if (part.type === "text") {
          const lines = String(part.value).split("\n");
          lines.forEach((line, i) => {
            if (i > 0) root.appendChild(document.createElement("br"));
            if (line) root.appendChild(document.createTextNode(line));
          });
        } else {
          const span = document.createElement("span");
          span.setAttribute("contenteditable", "false");
          span.dataset.mention = part.token;
          span.dataset.kind = part.kind || "image";
          span.className = "vs-mention-chip";
          span.textContent = `@${part.label}`;
          root.appendChild(span);
        }
      }
      return root;
    },
    [],
  );

  const serializeEditor = React.useCallback(() => {
    const root = editorRef.current;
    if (!root) return "";
    let out = "";
    const walk = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        out += node.textContent || "";
        return;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      if (node.dataset?.mention) {
        out += node.dataset.mention;
        return;
      }
      if (node.nodeName === "BR") {
        out += "\n";
        return;
      }
      // Contenteditable Enter often inserts <div>/<p> instead of <br>.
      const block =
        node !== root &&
        (node.nodeName === "DIV" || node.nodeName === "P");
      if (block && out.length && !out.endsWith("\n")) out += "\n";
      node.childNodes.forEach(walk);
      if (block && !out.endsWith("\n")) out += "\n";
    };
    root.childNodes.forEach(walk);
    return out.replace(/\n+$/, "");
  }, []);

  const applyStylesToChips = React.useCallback(() => {
    const root = editorRef.current;
    if (!root) return;
    root.querySelectorAll(".vs-mention-chip").forEach((el) => {
      const kind = el.dataset.kind || "image";
      const c = chipColors(kind, theme);
      el.style.color = c.color;
      el.style.background = c.bgcolor;
      el.style.border = c.border;
      el.style.borderRadius = "6px";
      el.style.padding = "1px 6px";
      el.style.margin = "0 1px";
      el.style.fontWeight = "700";
      el.style.fontSize = "0.8125rem";
      el.style.lineHeight = "1.5";
      el.style.display = "inline";
      el.style.cursor = "default";
      el.style.whiteSpace = "nowrap";
      el.style.verticalAlign = "baseline";
    });
  }, [theme]);

  // Sync external value → editor when not mid-keystroke
  React.useEffect(() => {
    const root = editorRef.current;
    if (!root || skipSyncRef.current) {
      skipSyncRef.current = false;
      return;
    }
    if (document.activeElement === root) return;
    root.innerHTML = "";
    root.appendChild(buildDomFromValue(value));
    applyStylesToChips();
  }, [value, buildDomFromValue, applyStylesToChips]);

  React.useEffect(() => {
    applyStylesToChips();
  }, [assets, applyStylesToChips]);

  const closeMenu = () => {
    setMenuOpen(false);
    setMenuQuery("");
    setMenuIndex(0);
    setAnchorEl(null);
    mentionStartRef.current = null;
  };

  const emitChange = () => {
    const next = serializeEditor();
    skipSyncRef.current = true;
    onChange?.(next);
  };

  const insertMention = (asset) => {
    const root = editorRef.current;
    if (!root || !asset) return;
    root.focus();

    const before = getTextBeforeCaret(root);
    const atMatch = before.match(/@([^\s\n[@]*)$/);
    if (atMatch) {
      deleteCharsBeforeCaret(root, atMatch[0].length);
    }

    const sel = window.getSelection();
    const chip = document.createElement("span");
    chip.setAttribute("contenteditable", "false");
    chip.dataset.mention = asset.token;
    chip.dataset.kind = asset.kind;
    chip.className = "vs-mention-chip";
    chip.textContent = `@${asset.label}`;
    const space = document.createTextNode("\u00A0");

    if (sel && sel.rangeCount > 0 && root.contains(sel.anchorNode)) {
      const range = sel.getRangeAt(0);
      range.deleteContents();
      range.insertNode(space);
      range.insertNode(chip);
      range.setStartAfter(space);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    } else {
      root.appendChild(chip);
      root.appendChild(space);
      placeCaretAtEnd(root);
    }

    applyStylesToChips();
    closeMenu();
    emitChange();
  };

  const detectMentionTrigger = React.useCallback(() => {
    const root = editorRef.current;
    if (!root) return;
    const before = getTextBeforeCaret(root);
    const atMatch = before.match(/@([^\s\n[@]*)$/);
    if (!atMatch) {
      setMenuOpen(false);
      setMenuQuery("");
      setMenuIndex(0);
      setAnchorEl(null);
      mentionStartRef.current = null;
      return;
    }
    const query = String(atMatch[1] || "");
    mentionStartRef.current = before.length - atMatch[0].length;
    setMenuQuery(query);
    setMenuIndex(0);
    setMenuOpen(true);
    setAnchorEl(root);
  }, []);

  // If user uploads while an @query is already in the prompt, reopen the picker.
  React.useEffect(() => {
    if (!assets.length) return;
    const root = editorRef.current;
    if (!root || document.activeElement !== root) return;
    detectMentionTrigger();
  }, [assets.length, detectMentionTrigger]);

  const onInput = () => {
    emitChange();
    // Defer so DOM/selection settle after the inserted character.
    window.requestAnimationFrame(() => detectMentionTrigger());
  };

  const onKeyDown = (e) => {
    const list = filtered;
    if (menuOpen) {
      if (list.length) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setMenuIndex((i) => (i + 1) % list.length);
          return;
        }
        if (e.key === "ArrowUp") {
          e.preventDefault();
          setMenuIndex((i) => (i - 1 + list.length) % list.length);
          return;
        }
        if (e.key === "Enter" || e.key === "Tab") {
          e.preventDefault();
          insertMention(list[menuIndex] || list[0]);
          return;
        }
      }
      if (e.key === "Escape") {
        e.preventDefault();
        closeMenu();
        return;
      }
    }
  };

  const onPaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
  };

  const minHeight = Math.max(2, Number(minRows) || 4) * 24;

  return (
    <Box sx={{ width: "100%" }}>
      {label ? (
        <InputLabel
          shrink
          sx={{
            position: "relative",
            transform: "none",
            mb: 0.75,
            fontSize: "0.75rem",
            fontWeight: 600,
            color: "text.secondary",
          }}
        >
          {label}
        </InputLabel>
      ) : null}

      <Box
        ref={mirrorRef}
        sx={{
          position: "relative",
          borderRadius: embedded ? 0 : 1,
          border: embedded ? "none" : `1px solid ${theme.palette.divider}`,
          bgcolor: embedded
            ? "transparent"
            : dark
              ? alpha("#fff", 0.03)
              : "background.paper",
          ...(embedded
            ? {}
            : {
                "&:focus-within": {
                  borderColor: "primary.main",
                  boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.4)}`,
                },
              }),
          opacity: disabled ? 0.55 : 1,
          pointerEvents: disabled ? "none" : "auto",
        }}
      >
        {!value && placeholder ? (
          <Typography
            variant="body2"
            color="text.disabled"
            sx={{
              position: "absolute",
              top: embedded ? 8 : 12,
              left: embedded ? 4 : 14,
              right: embedded ? 4 : 14,
              pointerEvents: "none",
              whiteSpace: "pre-wrap",
            }}
          >
            {placeholder}
          </Typography>
        ) : null}

        <Box
          ref={editorRef}
          role="textbox"
          aria-multiline
          aria-label={label || "Prompt"}
          contentEditable={!disabled}
          suppressContentEditableWarning
          spellCheck={false}
          data-gramm="false"
          data-gramm_editor="false"
          data-enable-grammarly="false"
          onInput={onInput}
          onKeyDown={onKeyDown}
          onKeyUp={detectMentionTrigger}
          onPaste={onPaste}
          onClick={(e) => {
            const chip = e.target?.closest?.(".vs-mention-chip");
            if (chip?.dataset?.mention) {
              const asset = assetByToken.get(chip.dataset.mention);
              if (asset) setHover({ asset, el: chip });
            }
            detectMentionTrigger();
          }}
          onMouseOver={(e) => {
            const chip = e.target?.closest?.(".vs-mention-chip");
            if (!chip?.dataset?.mention) return;
            const asset = assetByToken.get(chip.dataset.mention);
            // Also resolve by label walk of assets if token remapped
            const resolved =
              asset ||
              assets.find(
                (a) =>
                  a.token === chip.dataset.mention ||
                  `@${a.label}` === chip.textContent,
              );
            if (resolved) setHover({ asset: resolved, el: chip });
          }}
          onMouseOut={(e) => {
            const chip = e.target?.closest?.(".vs-mention-chip");
            if (chip && !chip.contains(e.relatedTarget)) {
              setHover({ asset: null, el: null });
            }
          }}
          sx={{
            minHeight,
            maxHeight: embedded ? 160 : 360,
            overflowY: "auto",
            px: embedded ? 0.5 : 1.75,
            py: embedded ? 0.75 : 1.5,
            outline: "none",
            fontSize: "0.875rem",
            lineHeight: 1.6,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            color: "text.primary",
            caretColor: theme.palette.primary.main,
          }}
        />
      </Box>

      {!embedded && helperText ? (
        <FormHelperText sx={{ mx: 1.75, mt: 0.75 }}>{helperText}</FormHelperText>
      ) : !embedded && assets.length ? (
        <FormHelperText sx={{ mx: 1.75, mt: 0.75 }}>
          {lang?.videoStudioMentionHint ||
            "Type @ to tag a character image. We auto-describe it so the AI keeps that exact likeness (face, hair, wardrobe) instead of regenerating a new person. Use Name: “line” for dialogue."}
        </FormHelperText>
      ) : !embedded ? (
        <FormHelperText sx={{ mx: 1.75, mt: 0.75 }}>
          {lang?.videoStudioMentionEmptyHint ||
            "Upload an image or video above, then type @ to tag it in your prompt."}
        </FormHelperText>
      ) : null}

      <HoverPreviewCard asset={hover.asset} anchorEl={hover.el} />

      <Popper
        open={menuOpen && Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement="bottom-start"
        modifiers={[{ name: "offset", options: { offset: [0, 6] } }]}
        sx={{ zIndex: theme.zIndex.modal }}
      >
        <ClickAwayListener onClickAway={closeMenu}>
          <Paper
            elevation={8}
            sx={{
              width: 320,
              maxHeight: 280,
              overflow: "auto",
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Box sx={{ px: 1.5, py: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="caption" fontWeight={800} color="text.secondary">
                {lang?.videoStudioMentionPickerTitle || "Reference assets"}
                {menuQuery ? ` · “${menuQuery}”` : ""}
              </Typography>
            </Box>
            {!filtered.length ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                {assets.length
                  ? lang?.videoStudioMentionNoMatch || "No matching uploads."
                  : lang?.videoStudioMentionNoAssets || "Upload an image or video first."}
              </Typography>
            ) : (
              <List dense disablePadding>
                {filtered.map((asset, i) => (
                  <ListItemButton
                    key={asset.key}
                    selected={i === menuIndex}
                    onMouseEnter={() => setMenuIndex(i)}
                    onClick={() => insertMention(asset)}
                    sx={{ py: 1, gap: 1 }}
                  >
                    <ListItemIcon sx={{ minWidth: 48 }}>
                      <MentionThumb asset={asset} size={40} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                          <Typography
                            variant="body2"
                            fontWeight={800}
                            sx={{ color: chipColors(asset.kind, theme).color }}
                          >
                            @{asset.label}
                          </Typography>
                        </Stack>
                      }
                      secondary={asset.name !== asset.label ? asset.name : asset.kind}
                    />
                    {asset.kind === "video" ? (
                      <VideocamOutlined fontSize="small" color="action" />
                    ) : (
                      <ImageOutlined fontSize="small" color="action" />
                    )}
                  </ListItemButton>
                ))}
              </List>
            )}
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
}
